import { defineStore } from "pinia";
import { Ref } from "vue";
import {
  INewShiftTemplate,
  IShiftTemplate,
  IUpdateShiftTemplate,
} from "@/features/shifts/shifts.types";
import { shiftService } from "@/features/shifts/shifts.service";
import { ITableHeader } from "@/types/table";
import { useNotification } from "@/utils/notification";
const { pushNotification } = useNotification();

export const useShiftStore = defineStore("shift", () => {
  const shiftsForFilter: IShiftTemplate[] = [];
  const shiftHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      fitContent: true,
    },
    {
      name: "title",
      label: "Shift name",
      width: "25%",
    },
    {
      name: "start_time",
      label: "Start",
      width: "15%",
    },
    {
      name: "end_time",
      label: "End",
      width: "15%",
    },
    {
      name: "is_active",
      label: "Status",
      width: "10%",
    },
    {
      name: "kpi",
      label: "KPI",
      width: "10%",
    },
    {
      name: "kpi_progress",
      label: "KPI Progress",
      width: "10%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "10%",
    },
  ];

  async function getShifts(
    loading: Ref<boolean>,
    isActive?: boolean
  ): Promise<IShiftTemplate[]> {
    try {
      loading.value = true;
      const { data } = await shiftService.getShifts(isActive);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function createShift(
    loading: Ref<boolean>,
    payload: INewShiftTemplate
  ) {
    try {
      loading.value = true;
      const { data } = await shiftService.createShift(payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function updateShift(
    loading: Ref<boolean>,
    payload: IUpdateShiftTemplate,
    id: string
  ) {
    try {
      loading.value = true;
      const { data } = await shiftService.updateShift(id, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function currentShift(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await shiftService.currentShift();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function runsShift(loading: Ref<boolean>, run_date: string) {
    try {
      loading.value = true;
      const { data } = await shiftService.runsShift(run_date);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function seedDefaultShift(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await shiftService.seedDefaultShift();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function addShiftMember(
    loading: Ref<boolean>,
    shiftRunId: string,
    userId: string
  ) {
    try {
      loading.value = true;
      const { data } = await shiftService.addShiftMember(shiftRunId, userId);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  return {
    shiftsForFilter,
    shiftHeaders,
    getShifts,
    createShift,
    updateShift,
    currentShift,
    runsShift,
    seedDefaultShift,
    addShiftMember,
  };
});
